import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms'
import { Subscription, Observable } from 'rxjs'
import { finalize } from 'rxjs/operators'
import { AccountService } from '../../services/account.service'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { FormValidationService } from 'src/app/services/form-validation.service'
import { handleArpioError } from 'src/utils/handle-error'
import { ApplicationDto, ApplicationType } from 'src/app/services/dtos'

// Shared interfaces
export interface ApplicationItem {
  id: string
  name: string
  type: string
  selected: boolean
  appId?: string // Original appId from API
}

export interface PolicyChild {
  policyName: string
  accessLevel: string
  allApplications: boolean
  selectedApplications: ApplicationItem[]
  collapsed?: boolean // Track collapsed state
}

export abstract class BaseRoleModal {
  // Common properties
  availableApplications: ApplicationItem[] = []
  loadingApplications = true
  accessLevels = [
    {
      value: 'app_admin',
      label: 'Administrator',
      description: 'Can do everything for applications, including manage, test, and recover applications',
    },
    {
      value: 'app_recover',
      label: 'Recovery Operator',
      description: 'Can launch Recovery, Recovery-related features, Failback, and all previous permissions',
    },
    {
      value: 'app_test',
      label: 'Test Operator',
      description: 'Can initiate and conclude failover tests, modify sandbox settings, and all previous permissions',
    },
    {
      value: 'app_manage',
      label: 'Manager',
      description:
        'Can modify application settings, resource lists, RPO, and notifications, but cannot initiate Tests or Failovers',
    },
    {
      value: 'app_read',
      label: 'Read-Only',
      description: 'Can view resource lists and application settings, but cannot modify anything',
    },
  ]

  // Form and subscriptions
  roleForm: FormGroup
  saveSubscription: Subscription = null
  saving = false

  // Policy state management
  policyCollapsedStates: boolean[] = [] // Track collapsed state for each policy
  protected defaultPolicyCollapsed = false // Default collapsed state for new policies - expand by default

  constructor(
    protected formBuilder: FormBuilder,
    protected accountService: AccountService,
    protected apiService: ApiService,
    protected uiService: UiService,
    protected accessLevelService: AccessLevelService,
    protected formValidationService: FormValidationService
  ) {
    this.initializeForm()
  }

  // Abstract methods that must be implemented by child components
  abstract getSaveButtonText(): string
  abstract performSave(rolePayload: any): Observable<any>
  abstract onSaveSuccess(result: any): void

  // Common form initialization
  protected initializeForm() {
    this.roleForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      externalId: ['', this.formValidationService.getExternalIdValidators()],
      policyChildren: this.formBuilder.array([]),
    })
  }

  // Common application loading
  protected loadAvailableApplications() {
    this.loadingApplications = true
    this.apiService.listApplications(this.accountService.accountId).subscribe(
      (applications) => {
        // Add 2 second delay to ensure applications are fully loaded and available for policy decsription generation
        setTimeout(() => {
          this.availableApplications = applications.map((app) => ({
            id: app.appId || app.name,
            name: app.name,
            type: app.type,
            selected: false,
          }))
          this.loadingApplications = false
          // Force change detection after applications are loaded
          this.refreshPolicyDisplays()
        }, 1000)
      },
      (error) => {
        console.error('Failed to load applications:', error)
        this.availableApplications = []
        this.loadingApplications = false
      }
    )
  }

  // The API returns lowercase enum values ('aws'/'azure'); the badge shows the branded form.
  appTypeLabel(type: string): string {
    if (type === ApplicationType.AWS) return 'AWS'
    if (type === ApplicationType.AZURE) return 'Azure'
    return type
  }

  // Force refresh of policy displays after applications load
  protected refreshPolicyDisplays(): void {
    // Trigger change detection by marking the form array as dirty
    if (this.policyChildrenArray) {
      this.policyChildrenArray.markAsDirty()
      this.policyChildrenArray.updateValueAndValidity()

      // Also force update each policy child form
      for (let i = 0; i < this.policyChildrenArray.length; i++) {
        const control = this.policyChildrenArray.at(i)
        if (control) {
          control.markAsDirty()
          control.updateValueAndValidity()
        }
      }
    }
  }

  // Common form validation
  get isFormValid() {
    const hasValidForm = this.roleForm.valid
    const policyChildrenArray = this.roleForm.get('policyChildren') as FormArray

    const hasValidPolicies =
      policyChildrenArray.length > 0 &&
      policyChildrenArray.controls.every((control) => {
        const accessLevel = control.get('accessLevel')?.value

        if (!accessLevel) {
          return false
        }

        // For app-level permissions, ensure either allApps is true or specific apps are selected
        if (this.accessLevelService.isAppLevelAccess(accessLevel)) {
          const allApps = control.get('allApplications')?.value
          const selectedApps = control.get('selectedApplications')?.value || []
          return allApps || selectedApps.length > 0
        }

        return true
      })

    return hasValidForm && hasValidPolicies
  }

  // Common error handling
  get nameErrors() {
    const control = this.roleForm.get('name')
    if (control?.errors && control?.touched) {
      if (control.errors['required']) {
        return 'Role name is required.'
      }
    }
    return null
  }

  get externalIdErrors() {
    return this.formValidationService.getExternalIdErrorsFromForm(this.roleForm, 'externalId')
  }

  // Common save logic
  onSave() {
    if (!this.isFormValid) return

    this.saving = true

    const formData = this.roleForm.value
    const policyChildren = formData.policyChildren || []

    // Convert policy children to the policies format expected by the API
    const policies = policyChildren.map((policy) => {
      const accessLevel = policy.accessLevel

      // Account-level permissions don't need app specifications
      if (this.accessLevelService.isAccountLevelAccess(accessLevel)) {
        return {
          accessLevel: accessLevel,
          allApps: null,
          appIds: null,
          appSetIds: null,
        }
      }

      // App-level permissions require app specification
      const isAllApps = policy.allApplications || false
      const selectedAppIds = isAllApps ? [] : (policy.selectedApplications || []).map((app) => app.id || app)
      const effectiveAllApps = isAllApps || (!isAllApps && selectedAppIds.length === 0)

      return {
        accessLevel: accessLevel,
        allApps: effectiveAllApps || null,
        appIds: effectiveAllApps ? null : selectedAppIds,
        appSetIds: null,
      }
    })

    // Create role payload
    const rolePayload = {
      name: formData.name,
      externalId: formData.externalId || null,
      policies: policies,
      isSystemRole: null,
    }

    this.saveSubscription = this.performSave(rolePayload)
      .pipe(
        finalize(() => {
          this.saving = false
          this.saveSubscription = null
        })
      )
      .subscribe(
        (result) => {
          this.onSaveSuccess(result)
        },
        (error) => {
          // Use standard error handling for all errors
          handleArpioError(window.location.href, this.apiService, this.uiService)(error)
        }
      )
  }

  // Policy children management
  get policyChildrenArray() {
    return this.roleForm.get('policyChildren') as FormArray
  }

  addPolicyChild() {
    // Check if we've reached the maximum policy limit
    const MAX_POLICIES = 10 // Reasonable limit, can be adjusted based on API constraints
    if (this.policyChildrenArray.length >= MAX_POLICIES) {
      alert('Maximum number of policies reached. You can have up to ' + MAX_POLICIES + ' policies per role.')
      return
    }

    const policyChild = this.formBuilder.group({
      accessLevel: ['', Validators.required],
      allApplications: [false],
      selectedApplications: [[]],
    })

    this.policyChildrenArray.push(policyChild)

    // If defaultPolicyCollapsed is false (create mode), keep all policies open
    // If defaultPolicyCollapsed is true (edit mode), close all other accordions and expand only the new one
    if (this.defaultPolicyCollapsed) {
      this.policyCollapsedStates = this.policyCollapsedStates.map(() => true)
    }
    this.policyCollapsedStates.push(this.defaultPolicyCollapsed)
  }

  addPolicyChildWithData(policyName: string, accessLevel: string, allApps: boolean, selectedApps: ApplicationItem[]) {
    // Check if we've reached the maximum policy limit
    const MAX_POLICIES = 10 // Reasonable limit, can be adjusted based on API constraints
    if (this.policyChildrenArray.length >= MAX_POLICIES) {
      alert('Maximum number of policies reached. You can have up to ' + MAX_POLICIES + ' policies per role.')
      return
    }

    const policyChild = this.formBuilder.group({
      accessLevel: [accessLevel, Validators.required],
      allApplications: [allApps],
      selectedApplications: [selectedApps],
    })

    this.policyChildrenArray.push(policyChild)

    // Initialize collapsed state
    this.policyCollapsedStates.push(this.defaultPolicyCollapsed)
  }

  removePolicyChild(index: number) {
    if (this.policyChildrenArray.length > 1) {
      this.policyChildrenArray.removeAt(index)

      // Remove the corresponding collapsed state
      this.policyCollapsedStates.splice(index, 1)
    }
  }

  onAllApplicationsChange(index: number, checked: boolean) {
    const childControl = this.policyChildrenArray.at(index)
    childControl.get('allApplications')?.setValue(checked)
    if (checked) {
      childControl.get('selectedApplications')?.setValue([])
    }
  }

  getPolicyChildApplications(index: number): ApplicationItem[] {
    const childControl = this.policyChildrenArray.at(index)
    const selectedApps = childControl.get('selectedApplications')?.value || []
    return selectedApps
  }

  setPolicyChildApplications(index: number, applications: ApplicationItem[]) {
    const childControl = this.policyChildrenArray.at(index)
    childControl.get('selectedApplications')?.setValue(applications)
  }

  applicationTrackBy(_index: number, application: ApplicationItem) {
    return application.id
  }

  // Generate policy name based on access level and applications
  generatePolicyName(index: number): string {
    const childControl = this.policyChildrenArray.at(index)
    const accessLevel = childControl.get('accessLevel')?.value
    const allApplications = childControl.get('allApplications')?.value
    const selectedApplications = childControl.get('selectedApplications')?.value || []

    if (!accessLevel) {
      return ``
    }

    const accessDisplay = this.getAccessLevelDisplay(accessLevel)

    // For account-level permissions
    if (this.accessLevelService.isAccountLevelAccess(accessLevel)) {
      return `${accessDisplay}`
    }

    // For app-level permissions
    if (allApplications) {
      return `${accessDisplay} for all applications `
    }

    if (selectedApplications.length > 0) {
      // Check if any application names are actually IDs (contain no spaces and are long alphanumeric)
      const hasValidNames = selectedApplications.every((app) => app.name && !this.isApplicationId(app.name))

      if (!hasValidNames) {
        return `` // Don't show anything if names aren't loaded yet
      }

      if (selectedApplications.length === 1) {
        return `${accessDisplay} for ${selectedApplications[0].name}`
      } else if (selectedApplications.length <= 3) {
        const appNames = selectedApplications.map((app) => app.name).join(', ')
        return `${accessDisplay} for ${appNames}`
      } else {
        return `${accessDisplay} for ${selectedApplications.length} applications`
      }
    }

    // Fallback
    return ``
  }

  // Helper method to detect if a string looks like an application ID
  private isApplicationId(name: string): boolean {
    // Check if it's a long alphanumeric string (likely an ID)
    return /^[a-zA-Z0-9]{20,}$/.test(name)
  }

  // Get access level display name
  getAccessLevelDisplay(accessLevel: string): string {
    return this.accessLevelService.getAccessLevelDisplayName(accessLevel as any)
  }

  // Get policy name components for template rendering with tooltip support
  getPolicyNameComponents(index: number): {
    prefix: string
    applicationsText: string
    suffix: string
    showTooltip: boolean
    tooltipText: string
  } {
    const childControl = this.policyChildrenArray.at(index)
    const accessLevel = childControl.get('accessLevel')?.value
    const allApplications = childControl.get('allApplications')?.value
    const selectedApplications = childControl.get('selectedApplications')?.value || []

    if (!accessLevel) {
      return {
        prefix: '',
        applicationsText: '',
        suffix: '',
        showTooltip: false,
        tooltipText: '',
      }
    }

    const accessDisplay = this.getAccessLevelDisplay(accessLevel)

    // For account-level permissions
    if (this.accessLevelService.isAccountLevelAccess(accessLevel)) {
      return {
        prefix: accessDisplay,
        applicationsText: '',
        suffix: '',
        showTooltip: false,
        tooltipText: '',
      }
    }

    // For app-level permissions
    if (allApplications) {
      return {
        prefix: `${accessDisplay} for `,
        applicationsText: 'all applications',
        suffix: '',
        showTooltip: false,
        tooltipText: '',
      }
    }

    if (selectedApplications.length > 0) {
      // Check if any application names are actually IDs
      const hasValidNames = selectedApplications.every(
        (app: ApplicationItem) => app.name && !this.isApplicationId(app.name)
      )

      if (!hasValidNames) {
        return {
          prefix: '',
          applicationsText: '',
          suffix: '',
          showTooltip: false,
          tooltipText: '',
        } // Don't show anything if names aren't loaded yet
      }

      if (selectedApplications.length === 1) {
        return {
          prefix: `${accessDisplay} for `,
          applicationsText: selectedApplications[0].name,
          suffix: '',
          showTooltip: false,
          tooltipText: '',
        }
      } else if (selectedApplications.length <= 3) {
        const appNames = selectedApplications.map((app: ApplicationItem) => app.name).join(', ')
        return {
          prefix: `${accessDisplay} for `,
          applicationsText: appNames,
          suffix: '',
          showTooltip: false,
          tooltipText: '',
        }
      } else {
        return {
          prefix: `${accessDisplay} for `,
          applicationsText: `${selectedApplications.length} applications`,
          suffix: '',
          showTooltip: true,
          tooltipText: selectedApplications
            .map((app: ApplicationItem) => `<span class="tooltip-badge">${app.name}</span>`)
            .join(''),
        }
      }
    }

    // Fallback
    return {
      prefix: '',
      applicationsText: '',
      suffix: '',
      showTooltip: false,
      tooltipText: '',
    }
  }

  getPolicyChildAccessLevel(index: number): string {
    const childControl = this.policyChildrenArray.at(index)
    return childControl.get('accessLevel')?.value || ''
  }

  getPolicyErrors(index: number): string | null {
    const childControl = this.policyChildrenArray.at(index)
    const accessLevel = childControl.get('accessLevel')?.value

    if (!accessLevel) return 'Access level is required.'

    if (this.accessLevelService.isAppLevelAccess(accessLevel)) {
      const allApps = childControl.get('allApplications')?.value
      const selectedApps = childControl.get('selectedApplications')?.value || []

      if (!allApps && selectedApps.length === 0) {
        return 'App-level permissions require either "All Applications" or specific applications to be selected'
      }
    }

    return null
  }

  isPolicyValid(index: number): boolean {
    return this.getPolicyErrors(index) === null
  }

  // Common cleanup
  protected resetSelections() {
    const policyChildrenArray = this.roleForm.get('policyChildren') as FormArray
    while (policyChildrenArray.length) {
      policyChildrenArray.removeAt(0)
    }

    this.availableApplications.forEach((app) => (app.selected = false))
  }

  protected cleanup() {
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe()
      this.saveSubscription = null
    }
  }

  // Tooltip methods for delay and hover functionality
  private tooltipHideTimer: any

  keepTooltipVisible() {
    if (this.tooltipHideTimer) {
      clearTimeout(this.tooltipHideTimer)
      this.tooltipHideTimer = null
    }
  }

  hideTooltipDelayed() {
    this.tooltipHideTimer = setTimeout(() => {
      // Logic to hide tooltip - this depends on the tooltip implementation
      // For now, we'll let the PrimeNG tooltip handle it naturally
    }, 200)
  }

  // Policy collapsible state management
  togglePolicyCollapsed(index: number): void {
    if (this.policyCollapsedStates[index] === undefined) {
      this.policyCollapsedStates[index] = this.defaultPolicyCollapsed
    }

    const wasCollapsed = this.policyCollapsedStates[index]

    // If opening this accordion, close all others
    if (wasCollapsed) {
      // Close all other accordions
      for (let i = 0; i < this.policyCollapsedStates.length; i++) {
        this.policyCollapsedStates[i] = true
      }
      // Open the clicked one
      this.policyCollapsedStates[index] = false
    } else {
      // Just close this one
      this.policyCollapsedStates[index] = true
    }
  }

  isPolicyCollapsed(index: number): boolean {
    // If state is undefined, use the default collapsed state
    if (this.policyCollapsedStates[index] === undefined) {
      return this.defaultPolicyCollapsed
    }
    return this.policyCollapsedStates[index]
  }

  // Custom checkbox methods for application selection
  areAllApplicationsSelected(policyIndex: number): boolean {
    const selectedApps = this.getPolicyChildApplications(policyIndex)
    return selectedApps.length > 0 && selectedApps.length === this.availableApplications.length
  }

  areSomeApplicationsSelected(policyIndex: number): boolean {
    const selectedApps = this.getPolicyChildApplications(policyIndex)
    return selectedApps.length > 0 && selectedApps.length < this.availableApplications.length
  }

  isApplicationSelected(policyIndex: number, application: ApplicationItem): boolean {
    const selectedApps = this.getPolicyChildApplications(policyIndex)
    const isSelected = selectedApps.some((app) => app.id === application.id)
    return isSelected
  }

  toggleAllApplications(policyIndex: number, event: Event): void {
    const target = event.target as HTMLInputElement
    const isChecked = target.checked

    if (isChecked) {
      // Select all applications
      this.setPolicyChildApplications(policyIndex, [...this.availableApplications])
    } else {
      // Deselect all applications
      this.setPolicyChildApplications(policyIndex, [])
    }
  }

  toggleApplication(policyIndex: number, application: ApplicationItem, event: Event): void {
    const target = event.target as HTMLInputElement
    const isChecked = target.checked
    const currentSelection = this.getPolicyChildApplications(policyIndex)

    if (isChecked) {
      // Add application to selection
      const newSelection = [...currentSelection, application]
      this.setPolicyChildApplications(policyIndex, newSelection)
    } else {
      // Remove application from selection
      const newSelection = currentSelection.filter((app) => app.id !== application.id)
      this.setPolicyChildApplications(policyIndex, newSelection)
    }
  }
}
